import { useCallback, useEffect, useMemo, useState } from "react";

import {
  isAwsDashboardDataProviderEnabled,
  signAwsDashboardMedia,
} from "../providers/aws";
import { getSupabaseClient } from "../providers/supabase/supabase";

export type MediaUrlSource = {
  url?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
  storage_is_public?: boolean | null;
};

type MediaUrlState =
  | { status: "idle"; url: string | null; error?: undefined }
  | { status: "loading"; url: string | null; error?: undefined }
  | { status: "loaded"; url: string; error?: undefined }
  | { status: "error"; url: string | null; error: string };

const defaultSignedUrlTtlSeconds = 60 * 60;
const maxCachedMediaUrls = 250;

type MediaUrlCacheEntry = {
  url: string;
  expiresAt: number;
};

const mediaUrlCache = new Map<string, MediaUrlCacheEntry>();
const pendingMediaUrls = new Map<string, Promise<string>>();

const mediaUrlCacheKey = (
  source: {
    storage_bucket: string | null;
    storage_path: string | null;
    storage_is_public: boolean;
  },
  expiresIn: number,
  backend: "aws" | "supabase",
): string | null =>
  source.storage_bucket && source.storage_path
    ? [
        backend,
        source.storage_bucket,
        source.storage_path,
        source.storage_is_public ? "public" : "private",
        String(expiresIn),
      ].join("|")
    : null;

const readCachedMediaUrl = (key: string): string | null => {
  const cached = mediaUrlCache.get(key);
  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) {
    mediaUrlCache.delete(key);
    return null;
  }
  // Refresh insertion order so the size cap behaves as a small LRU cache.
  mediaUrlCache.delete(key);
  mediaUrlCache.set(key, cached);
  return cached.url;
};

const cacheMediaUrl = (key: string, url: string, expiresAt: number) => {
  mediaUrlCache.delete(key);
  mediaUrlCache.set(key, { url, expiresAt });
  while (mediaUrlCache.size > maxCachedMediaUrls) {
    const oldest = mediaUrlCache.keys().next().value as string | undefined;
    if (!oldest) break;
    mediaUrlCache.delete(oldest);
  }
};

const signedUrlExpiry = (expiresIn: number): number => {
  const safetyWindow = Math.min(60, expiresIn * 0.1);
  return Date.now() + Math.max(1, expiresIn - safetyWindow) * 1000;
};

const resolveCachedMediaUrl = (
  key: string,
  resolve: () => Promise<string>,
  expiresAt: number,
): Promise<string> => {
  const cached = readCachedMediaUrl(key);
  if (cached) return Promise.resolve(cached);

  const pending = pendingMediaUrls.get(key);
  if (pending) return pending;

  const request = resolve()
    .then((url) => {
      cacheMediaUrl(key, url, expiresAt);
      return url;
    })
    .finally(() => {
      if (pendingMediaUrls.get(key) === request) {
        pendingMediaUrls.delete(key);
      }
    });
  pendingMediaUrls.set(key, request);
  return request;
};

export const clearMediaUrlCacheForTests = () => {
  mediaUrlCache.clear();
  pendingMediaUrls.clear();
};

/**
 * Imperative sibling of `useMediaUrl`, sharing its cache and in-flight dedupe.
 *
 * Some callers need to sign a list whose length varies per record (the Director
 * View measures every narration clip), which a hook per item cannot express.
 * Returns null when the source carries no storage path and no direct url.
 */
export const signMediaSource = async (
  source: MediaUrlSource,
  options?: { expiresIn?: number },
): Promise<string | null> => {
  const expiresIn = options?.expiresIn ?? defaultSignedUrlTtlSeconds;
  const bucket = source.storage_bucket ?? null;
  const path = source.storage_path ?? null;
  const isPublic = source.storage_is_public ?? false;

  if (!bucket || !path) return source.url ?? null;

  const backend = isAwsDashboardDataProviderEnabled() ? "aws" : "supabase";
  const key = mediaUrlCacheKey(
    { storage_bucket: bucket, storage_path: path, storage_is_public: isPublic },
    expiresIn,
    backend,
  );
  if (!key) return source.url ?? null;

  const resolve = async (): Promise<string> => {
    if (backend === "aws") {
      const { url } = await signAwsDashboardMedia({
        bucket,
        key: path,
        expiresIn,
      });
      return url;
    }
    const storage = getSupabaseClient().storage.from(bucket);
    if (isPublic) {
      return storage.getPublicUrl(path).data.publicUrl;
    }
    const { data, error } = await storage.createSignedUrl(path, expiresIn);
    if (error || !data?.signedUrl) {
      throw new Error(error?.message ?? "Could not create a signed media URL");
    }
    return data.signedUrl;
  };

  const expiresAt =
    backend === "supabase" && isPublic
      ? Number.POSITIVE_INFINITY
      : signedUrlExpiry(expiresIn);
  return resolveCachedMediaUrl(key, resolve, expiresAt);
};

export function useMediaUrl(
  source: MediaUrlSource | null | undefined,
  options?: { expiresIn?: number },
) {
  const [refreshToken, setRefreshToken] = useState(0);
  const expiresIn = options?.expiresIn ?? defaultSignedUrlTtlSeconds;
  const stableSource = useMemo(
    () => ({
      url: source?.url ?? null,
      storage_bucket: source?.storage_bucket ?? null,
      storage_path: source?.storage_path ?? null,
      storage_is_public: source?.storage_is_public ?? false,
    }),
    [
      source?.url,
      source?.storage_bucket,
      source?.storage_path,
      source?.storage_is_public,
    ],
  );
  const backend = isAwsDashboardDataProviderEnabled() ? "aws" : "supabase";
  const cacheKey = useMemo(
    () => mediaUrlCacheKey(stableSource, expiresIn, backend),
    [backend, expiresIn, stableSource],
  );
  const [state, setState] = useState<MediaUrlState>(() => {
    const cached = cacheKey ? readCachedMediaUrl(cacheKey) : null;
    return cached
      ? { status: "loaded", url: cached }
      : stableSource.url
        ? { status: "loaded", url: stableSource.url }
        : { status: "idle", url: null };
  });

  useEffect(() => {
    let active = true;

    if (!stableSource.storage_bucket || !stableSource.storage_path) {
      setState(
        stableSource.url
          ? { status: "loaded", url: stableSource.url }
          : { status: "idle", url: null },
      );
      return () => {
        active = false;
      };
    }

    const resolveUrl = async (): Promise<string> => {
      if (backend === "aws") {
        const { url } = await signAwsDashboardMedia({
          bucket: stableSource.storage_bucket!,
          key: stableSource.storage_path!,
          expiresIn,
        });
        return url;
      }

      const storage = getSupabaseClient().storage.from(
        stableSource.storage_bucket!,
      );

      if (stableSource.storage_is_public) {
        const { data } = storage.getPublicUrl(stableSource.storage_path!);
        return data.publicUrl;
      }

      const { data, error } = await storage.createSignedUrl(
        stableSource.storage_path!,
        expiresIn,
      );
      if (error || !data?.signedUrl) {
        throw new Error(
          error?.message ?? "Could not create a signed media URL",
        );
      }
      return data.signedUrl;
    };

    const cached = cacheKey ? readCachedMediaUrl(cacheKey) : null;
    if (cached) {
      setState({ status: "loaded", url: cached });
    } else {
      setState({ status: "loading", url: stableSource.url });
      const expiresAt =
        backend === "supabase" && stableSource.storage_is_public
          ? Number.POSITIVE_INFINITY
          : signedUrlExpiry(expiresIn);
      const request = cacheKey
        ? resolveCachedMediaUrl(cacheKey, resolveUrl, expiresAt)
        : resolveUrl();
      request
        .then((url) => {
          if (active) setState({ status: "loaded", url });
        })
        .catch((error) => {
          if (!active) return;
          setState({
            status: "error",
            url: stableSource.url,
            error:
              error instanceof Error ? error.message : "Could not load media",
          });
        });
    }

    return () => {
      active = false;
    };
  }, [
    backend,
    cacheKey,
    expiresIn,
    refreshToken,
    stableSource.storage_bucket,
    stableSource.storage_is_public,
    stableSource.storage_path,
    stableSource.url,
  ]);

  const refresh = useCallback(() => {
    if (cacheKey) {
      mediaUrlCache.delete(cacheKey);
    }
    setRefreshToken((current) => current + 1);
  }, [cacheKey]);

  return {
    ...state,
    refresh,
    isLoading: state.status === "loading",
  };
}

export function sessionFinalVideoSource(source: {
  final_video_url?: string | null;
  final_video_storage_bucket?: string | null;
  final_video_storage_path?: string | null;
}) {
  return {
    url: source.final_video_url,
    storage_bucket: source.final_video_storage_bucket,
    storage_path: source.final_video_storage_path,
    storage_is_public: false,
  } satisfies MediaUrlSource;
}

export function sessionScriptSource(source: {
  script_url?: string | null;
  script_storage_bucket?: string | null;
  script_storage_path?: string | null;
}) {
  return {
    url: source.script_url,
    storage_bucket: source.script_storage_bucket,
    storage_path: source.script_storage_path,
    storage_is_public: false,
  } satisfies MediaUrlSource;
}
